import React from 'react';

import { Col, Row } from './Flex';
import { Text } from './Text';

import { useA11y, useComponentDefaultProps } from 'domain/hooks';
import { DivProps, SpanProps } from './common-types';
import { createId } from 'domain/utils';
import { cn } from 'domain/core';
import { commonInputClassName } from './Common';

type IListContext = {
	id: string;
	size: 'sm' | 'md' | 'lg';
};

const ListContext = React.createContext<IListContext>({
	id: '',
	size: 'md',
});

const useListContext = () => React.useContext(ListContext);

const defaultProps: Partial<ListProps> = {
	id: createId('list_'),
};

interface ListProps extends React.PropsWithChildren {
	id?: string;
	size?: 'sm' | 'md' | 'lg';
}
const List = (props: ListProps) => {
	const _props = useComponentDefaultProps(defaultProps, props);

	return (
		<ListContext.Provider value={{ id: _props.id!, size: _props.size || 'md' }}>
			{_props.children}
		</ListContext.Provider>
	);
};

type WrapperProps = Omit<DivProps, 'id'>;
const Wrapper = React.forwardRef(
	({ className, role = 'table', ...props }: WrapperProps, ref: any) => {
		const { id, size } = useListContext();

		const sizeClassName = useCellSize();

		return (
			<Col
				className={cn(
					'list',
					'w-auto !flex-nowrap gap-2 text-sm',
					sizeClassName,
					className
				)}
				id={id}
				{...props}
				ref={ref}
				role={role}
				aria-describedby={`${id}_description`}
			/>
		);
	}
);

type RowGroupProps = DivProps;
const RowGroup = React.forwardRef(
	({ className, role = 'rowgroup', ...props }: RowGroupProps, ref: any) => {
		return (
			<Col
				className={cn('list-row-group', 'gap-1', 'h-auto', className)}
				role={role}
				{...props}
				ref={ref}
			/>
		);
	}
);

type DescriptionProps = Omit<SpanProps, 'id'>;
const Description = React.forwardRef(
	({ className, ...props }: DescriptionProps, ref: any) => {
		const { id } = useListContext();

		return (
			<Text
				id={`${id}_description`}
				className={cn('list-description', 'text-sm italic', className)}
				{...props}
				ref={ref}
			/>
		);
	}
);

type ListRowProps = DivProps;
const ListRow = React.forwardRef(
	({ className, role = 'row', ...props }: ListRowProps, ref: any) => {
		return (
			<Row
				className={cn('list-row', 'rounded', 'transition-colors', className)}
				{...props}
				ref={ref}
				role={role}
			/>
		);
	}
);

const useCellSize = () => {
	const { size } = useListContext();

	return React.useMemo(() => {
		switch (size) {
			case 'sm':
				return 'text-xs';
			case 'md':
				return commonInputClassName;
		}
	}, [size]);
};

type HeaderCellProps = DivProps;
const HeaderCell = React.forwardRef(
	(
		{ className, role = 'columnheader', ...props }: HeaderCellProps,
		ref: any
	) => {
		const [title, ariaLabel] = useA11y({
			...props,
			ariaLabelPrefix: 'List header',
		});

		const sizeClassName = useCellSize();

		return (
			<Row
				role={role}
				title={title}
				aria-label={ariaLabel}
				className={cn(
					'list-header flex',
					'table-header-group truncate font-semibold',
					sizeClassName,
					className
				)}
				ref={ref}
				{...props}
			/>
		);
	}
);

type CellProps = DivProps;
const Cell = React.forwardRef(
	({ className, role = 'cell', ...props }: CellProps, ref: any) => {
		const [title, ariaLabel] = useA11y(props);

		const sizeClassName = useCellSize();

		return (
			<Row
				className={cn(
					'list-cell',
					'table-cell truncate',
					sizeClassName,
					className
				)}
				title={title}
				aria-label={ariaLabel}
				role={role}
				{...props}
				ref={ref}
			/>
		);
	}
);

Wrapper.displayName = 'Wrapper';
RowGroup.displayName = 'RowGroup';
Description.displayName = 'Description';
ListRow.displayName = 'ListRow';
HeaderCell.displayName = 'HeaderCell';
Cell.displayName = 'Cell';
List.displayName = 'List';
List.Wrapper = Wrapper;
List.RowGroup = RowGroup;
List.Row = ListRow;
List.Header = HeaderCell;
List.Cell = Cell;
List.Description = Description;

export { List };
